/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

/**
 * Hook for loading SplitDiffView data using Jotai atoms.
 *
 * This hook is now fully migrated to Jotai. It no longer uses Recoil for
 * line-to-position mapping - that is now computed natively in Jotai via
 * gitHubPullRequestComputedLineToPositionForFileAtom.
 */

import type {DiffAndTokenizeResponse} from '../../diffServiceWorker';
import type {DiffCommitIDs} from '../../github/diffTypes';
import type {GitHubPullRequestReviewThread} from '../../github/pullRequestTimelineTypes';

import {diffAndTokenizeAtom} from '../../diffServiceClient';
import {DiffSide} from '../../generated/graphql';
import {
  gitHubDiffCommitIDsAtom,
  gitHubPullRequestAtom,
  gitHubPullRequestCanAddCommentAtom,
  gitHubPullRequestComparableVersionsAtom,
  gitHubPullRequestComputedLineToPositionForFileAtom,
  gitHubPullRequestLineToPositionForFileAtom,
  gitHubPullRequestDragCurrentAtom,
  gitHubPullRequestDragStartAtom,
  gitHubPullRequestHighlightDragCurrentAtom,
  gitHubPullRequestHighlightDragStartAtom,
  gitHubPullRequestSelectedLinesAtom,
  gitHubPullRequestNewCommentInputCellAtom,
  gitHubPullRequestSelectedVersionIndexAtom,
  gitHubPullRequestVersionsAtom,
  gitHubThreadsForDiffFileAtom,
  hideWhitespaceAtom,
  notificationMessageAtom,
} from '../atoms';
import {useAtomValue, useSetAtom, useStore} from 'jotai';
import {loadable, unwrap} from 'jotai/utils';
import {useCallback, useEffect, useMemo, useRef} from 'react';

// ---- DOM-based drag highlight helpers ----
// These bypass React/Jotai entirely during drag for zero-overhead visual feedback.

function applyDragHighlight(
  table: HTMLTableElement,
  path: string,
  side: string,
  startLine: number,
  endLine: number,
  mode: 'comment' | 'highlight',
): void {
  const min = Math.min(startLine, endLine);
  const max = Math.max(startLine, endLine);
  const lineClass = mode === 'comment' ? 'drag-comment-line' : 'drag-highlight-line';
  const contentClass = mode === 'comment' ? 'drag-comment-content' : 'drag-highlight-content';
  const cells = table.querySelectorAll<HTMLTableCellElement>(
    `td.lineNumber[data-path="${CSS.escape(path)}"][data-side="${side}"]`,
  );
  cells.forEach(cell => {
    const line = parseInt(cell.dataset.lineNumber ?? '', 10);
    const inRange = !isNaN(line) && line >= min && line <= max;
    cell.classList.toggle(lineClass, inRange);
    const contentCell = cell.nextElementSibling;
    contentCell?.classList.toggle(contentClass, inRange);
    if (mode === 'comment') {
      contentCell?.classList.toggle('drag-comment-endpoint', inRange && (line === min || line === max));
    }
  });
}

function clearDragHighlight(table: HTMLTableElement): void {
  const classes = ['drag-comment-line', 'drag-comment-endpoint', 'drag-comment-content', 'drag-highlight-line', 'drag-highlight-content'];
  for (const cls of classes) {
    table.querySelectorAll(`.${cls}`).forEach(el => el.classList.remove(cls));
  }
}

/**
 * Type for the new comment input callbacks.
 */
export type NewCommentInputCallbacks = {
  onShowNewCommentInput: (event: React.MouseEvent<HTMLTableElement>) => void;
  /** Mouse down on plus button → start comment drag. */
  onMouseDownPlusButton: (event: React.MouseEvent<HTMLTableElement>) => void;
  /** Mouse down on line number → start highlight drag. */
  onMouseDownLineNumber: (event: React.MouseEvent<HTMLTableElement>) => void;
  onMouseMoveLineNumber: (event: React.MouseEvent<HTMLTableElement>) => void;
  onMouseUpLineNumber: (event: React.MouseEvent<HTMLTableElement>) => void;
  onResetNewCommentInput: () => void;
};

export type SplitDiffViewLoadableState =
  | {state: 'loading'}
  | {state: 'hasError'; error: Error}
  | {
      state: 'hasValue';
      data: {
        diffAndTokenize: DiffAndTokenizeResponse;
        threads: {[key in DiffSide]: GitHubPullRequestReviewThread[]} | null;
        newCommentInputCallbacks: NewCommentInputCallbacks | null;
        commitIDs: DiffCommitIDs | null;
      };
    };

/**
 * A fully Jotai-based hook for loading SplitDiffView data.
 *
 * All data sources are now Jotai atoms:
 * - commitIDs comes from gitHubDiffCommitIDsAtom
 * - threads are computed natively in Jotai via gitHubThreadsForDiffFileAtom
 * - Comment input callbacks use Jotai atoms
 * - diffAndTokenize uses the Jotai diffAndTokenizeAtom
 * - lineToPosition uses gitHubPullRequestComputedLineToPositionForFileAtom
 *
 * Returns a loadable-like object with state: 'loading' | 'hasError' | 'hasValue'
 */
export function useSplitDiffViewData(
  path: string,
  before: string | null,
  after: string | null,
  scopeName: string | null,
  colorMode: 'day' | 'night',
  isPullRequest: boolean,
): SplitDiffViewLoadableState {
  // Use Jotai for commitIDs (migrated atom)
  const loadableCommitIDsAtom = useMemo(() => loadable(gitHubDiffCommitIDsAtom), []);
  const commitIDsLoadable = useAtomValue(loadableCommitIDsAtom);

  // Read Jotai atoms for versions - now computed natively in Jotai
  // Note: These are read to ensure they're loaded, but not directly used in the return value
  // We use loadable to avoid suspending the whole component
  const loadableVersionsAtom = useMemo(() => loadable(gitHubPullRequestVersionsAtom), []);
  const versionsLoadable = useAtomValue(loadableVersionsAtom);
  useAtomValue(gitHubPullRequestSelectedVersionIndexAtom);

  // Use unwrap to avoid suspending on PR refresh.
  // When a comment is submitted, gitHubPullRequestAtom updates, which causes
  // gitHubThreadsForDiffFileAtom to re-evaluate (new promise). Without unwrap,
  // this suspends and the diff area shows a spinner.
  // The fallback retains the previous resolved value while re-evaluating.
  const unwrappedThreadsAtom = useMemo(
    () => unwrap(gitHubThreadsForDiffFileAtom(path), prev => prev ?? null),
    [path],
  );
  const threads = useAtomValue(unwrappedThreadsAtom);

  // Jotai atom for line-to-position mapping for this file path
  const lineToPositionAtom = useMemo(
    () => gitHubPullRequestLineToPositionForFileAtom(path),
    [path],
  );
  const setJotaiLineToPosition = useSetAtom(lineToPositionAtom);

  // Jotai-based comment input callbacks
  const store = useStore();
  const pullRequest = useAtomValue(gitHubPullRequestAtom);
  const setCellAtom = useSetAtom(gitHubPullRequestNewCommentInputCellAtom);
  const setNotification = useSetAtom(notificationMessageAtom);

  const setDragStart = useSetAtom(gitHubPullRequestDragStartAtom);
  const setDragCurrent = useSetAtom(gitHubPullRequestDragCurrentAtom);
  const setHighlightDragStart = useSetAtom(gitHubPullRequestHighlightDragStartAtom);
  const setHighlightDragCurrent = useSetAtom(gitHubPullRequestHighlightDragCurrentAtom);
  const setSelectedLines = useSetAtom(gitHubPullRequestSelectedLinesAtom);

  // Ref to store the table element during an active drag, used by the
  // document-level mouseup handler which doesn't have event.currentTarget.
  const dragTableRef = useRef<HTMLTableElement | null>(null);

  type LineInfo = {lineNumber: number; path: string; side: DiffSide};

  /** Parse a DiffSide from a dataset string. */
  const parseSide = useCallback((sideStr: string | undefined): DiffSide | null => {
    if (sideStr === DiffSide.Left) return DiffSide.Left;
    if (sideStr === DiffSide.Right) return DiffSide.Right;
    return null;
  }, []);

  /** Extract line number info from a mouse event on a line-number <td> or its child. */
  const parseLineNumberEvent = useCallback(
    (event: React.MouseEvent<HTMLTableElement>): LineInfo | null => {
      let el = event.target as HTMLElement | null;
      // Walk up to find the <td> (plus button or line number cell)
      while (el != null && !(el instanceof HTMLTableCellElement)) {
        el = el.parentElement;
      }
      if (el == null || !(el instanceof HTMLTableCellElement)) {
        return null;
      }
      // If we landed on a content cell (plus button click), use the preceding line number cell
      if (!el.classList.contains('lineNumber') && el.previousElementSibling instanceof HTMLTableCellElement) {
        el = el.previousElementSibling;
      }
      const {lineNumber: lineNumberStr, path, side: sideStr} = el.dataset;
      if (lineNumberStr == null || path == null || sideStr == null) {
        return null;
      }
      const lineNumber = parseInt(lineNumberStr, 10);
      const side = parseSide(sideStr);
      if (isNaN(lineNumber) || side == null) {
        return null;
      }
      return {lineNumber, path, side};
    },
    [parseSide],
  );

  /**
   * Find line info by walking up to the <tr> and finding the line number cell
   * for the given side. Works even when the event target is a content cell.
   */
  const findLineFromRow = useCallback(
    (event: MouseEvent | React.MouseEvent, targetSide: DiffSide): LineInfo | null => {
      let el = event.target as HTMLElement | null;
      while (el != null && el.tagName !== 'TR') {
        el = el.parentElement;
      }
      if (el == null) return null;

      const cells = el.querySelectorAll<HTMLTableCellElement>('td.lineNumber');
      for (let i = 0; i < cells.length; i++) {
        const cell = cells[i];
        const side = parseSide(cell.dataset.side);
        if (side === targetSide && cell.dataset.lineNumber != null && cell.dataset.path != null) {
          const lineNumber = parseInt(cell.dataset.lineNumber, 10);
          if (!isNaN(lineNumber)) {
            return {lineNumber, path: cell.dataset.path, side};
          }
        }
      }
      return null;
    },
    [parseSide],
  );

  /** Check if the event target was a plus (add comment) button. */
  const isPlusButtonEvent = useCallback(
    (event: React.MouseEvent<HTMLTableElement>): boolean => {
      const el = event.target;
      return el instanceof HTMLElement && el.dataset.addComment === 'true';
    },
    [],
  );

  /** Check if commenting is allowed on a given line and show a notification if not.
   *  Reads versions via store.get (non-reactive) to avoid destabilizing the
   *  callback identity, which would cascade through the memo chain. */
  const checkCanComment = useCallback(
    (lineNumber: number, path: string, side: DiffSide): boolean => {
      const canAddComment = store.get(
        gitHubPullRequestCanAddCommentAtom({lineNumber, path, side}),
      );
      if (!canAddComment) {
        try {
          const vLoadable = store.get(loadableVersionsAtom);
          if (vLoadable.state !== 'hasData') throw new Error('not loaded');
          const versions = vLoadable.data;
          const comparableVersions = store.get(gitHubPullRequestComparableVersionsAtom);
          const latestVersion = versions[versions.length - 1];
          const isLatestAfter =
            comparableVersions?.afterCommitID === latestVersion?.headCommit;

          if (!isLatestAfter) {
            setNotification({
              type: 'info',
              message:
                'Comments can only be added when viewing the latest version of the pull request.',
            });
          } else if (comparableVersions?.beforeCommitID != null && side === DiffSide.Left) {
            setNotification({
              type: 'info',
              message:
                'Comments cannot be added to the left side when comparing versions. The left side shows an older revision that is no longer part of the pull request.',
            });
          }
        } catch {
          // Versions not yet loaded — skip notification
        }
        return false;
      }
      return true;
    },
    [store, setNotification, loadableVersionsAtom],
  );

  /** Compute SHA256 of file path for URL hash (matches GitHub's format). */
  const computePathHash = useCallback(async (filePath: string): Promise<string> => {
    const encoder = new TextEncoder();
    const data = encoder.encode(filePath);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }, []);

  /** Update URL hash to reflect the selected line range. */
  const updateUrlHash = useCallback(
    async (filePath: string, side: DiffSide, startLine: number, endLine: number) => {
      const hash = await computePathHash(filePath);
      const prefix = side === DiffSide.Left ? 'L' : 'R';
      const lineRef = startLine === endLine
        ? `${prefix}${startLine}`
        : `${prefix}${startLine}-${prefix}${endLine}`;
      const newHash = `#diff-${hash}${lineRef}`;
      window.history.replaceState(null, '', newHash);
    },
    [computePathHash],
  );

  /** No-op click handler (selection is handled by mousedown/mouseup). */
  const onShowNewCommentInput = useCallback(
    (_event: React.MouseEvent<HTMLTableElement>) => {},
    [],
  );

  /**
   * Complete a comment drag: compute the range and open the comment input.
   * Uses the drag current atom (updated during drag) to determine the end position.
   */
  const completeCommentDrag = useCallback(
    (dragStart: LineInfo, endInfo: LineInfo | null) => {
      // Clear DOM drag classes before React takes over
      if (dragTableRef.current) {
        clearDragHighlight(dragTableRef.current);
        dragTableRef.current = null;
      }
      setDragStart(null);
      setDragCurrent(null);

      const anchor = endInfo ?? store.get(gitHubPullRequestDragCurrentAtom) ?? dragStart;

      const startLine = Math.min(dragStart.lineNumber, anchor.lineNumber);
      const endLine = Math.max(dragStart.lineNumber, anchor.lineNumber);

      if (startLine === endLine) {
        setCellAtom({path: anchor.path, lineNumber: endLine, side: anchor.side});
      } else {
        setCellAtom({
          path: anchor.path,
          lineNumber: endLine,
          side: anchor.side,
          startLine,
          startSide: anchor.side,
        });
      }
    },
    [store, setDragStart, setDragCurrent, setCellAtom],
  );

  /**
   * Complete a highlight drag: set the selected lines and update URL hash.
   */
  const completeHighlightDrag = useCallback(
    (dragStart: LineInfo, endInfo: LineInfo | null) => {
      // Clear DOM drag classes before React takes over
      if (dragTableRef.current) {
        clearDragHighlight(dragTableRef.current);
        dragTableRef.current = null;
      }
      setHighlightDragStart(null);
      setHighlightDragCurrent(null);

      const anchor = endInfo ?? store.get(gitHubPullRequestHighlightDragCurrentAtom) ?? dragStart;

      const startLine = Math.min(dragStart.lineNumber, anchor.lineNumber);
      const endLine = Math.max(dragStart.lineNumber, anchor.lineNumber);

      setSelectedLines({path: anchor.path, startLine, endLine, side: anchor.side});
      updateUrlHash(anchor.path, anchor.side, startLine, endLine);
    },
    [store, setHighlightDragStart, setHighlightDragCurrent, setSelectedLines, updateUrlHash],
  );

  /**
   * Document-level mouseup handler: finishes any active drag even when the
   * mouse is released outside the diff table. Registered on mousedown,
   * removed after firing once.
   */
  const documentMouseUpHandler = useCallback(
    () => {
      const commentDragStart = store.get(gitHubPullRequestDragStartAtom);
      if (commentDragStart != null) {
        completeCommentDrag(commentDragStart, null);
        return;
      }
      const highlightDragStart = store.get(gitHubPullRequestHighlightDragStartAtom);
      if (highlightDragStart != null) {
        completeHighlightDrag(highlightDragStart, null);
        return;
      }
    },
    [store, completeCommentDrag, completeHighlightDrag],
  );

  // ---- Plus button: Comment creation drag ----

  /** Mouse down on plus button: start comment drag. */
  const onMouseDownPlusButton = useCallback(
    (event: React.MouseEvent<HTMLTableElement>) => {
      if (!isPlusButtonEvent(event)) return;
      const info = parseLineNumberEvent(event);
      if (info == null) return;
      if (!checkCanComment(info.lineNumber, info.path, info.side)) return;
      event.preventDefault();
      // Store table ref for document mouseup and DOM manipulation
      dragTableRef.current = event.currentTarget;
      // Clear any highlight selection and its URL hash
      setSelectedLines(null);
      setHighlightDragStart(null);
      setHighlightDragCurrent(null);
      if (window.location.hash) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
      setDragStart(info);
      setDragCurrent(info);
      // Apply initial DOM highlight (single line)
      applyDragHighlight(event.currentTarget, info.path, info.side, info.lineNumber, info.lineNumber, 'comment');
      // Register document-level mouseup to handle releases outside the table
      document.addEventListener('mouseup', documentMouseUpHandler, {once: true});
    },
    [parseLineNumberEvent, isPlusButtonEvent, checkCanComment, setDragStart, setDragCurrent, setSelectedLines, setHighlightDragStart, setHighlightDragCurrent, documentMouseUpHandler],
  );

  // ---- Line number: Highlight selection drag ----

  /** Mouse down on line number: start highlight drag. */
  const onMouseDownLineNumber = useCallback(
    (event: React.MouseEvent<HTMLTableElement>) => {
      // Only handle clicks on actual line number cells, not content cells or plus buttons
      if (isPlusButtonEvent(event)) return;
      let el = event.target as HTMLElement | null;
      while (el != null && !(el instanceof HTMLTableCellElement)) {
        el = el.parentElement;
      }
      if (!(el instanceof HTMLTableCellElement) || !el.classList.contains('lineNumber')) return;
      const info = parseLineNumberEvent(event);
      if (info == null) return;
      event.preventDefault();
      // Store table ref for document mouseup and DOM manipulation
      dragTableRef.current = event.currentTarget;
      // Clear any comment input and previous selection
      setCellAtom(null);
      setDragStart(null);
      setDragCurrent(null);
      setSelectedLines(null);
      setHighlightDragStart(info);
      setHighlightDragCurrent(info);
      // Apply initial DOM highlight (single line)
      applyDragHighlight(event.currentTarget, info.path, info.side, info.lineNumber, info.lineNumber, 'highlight');
      // Register document-level mouseup to handle releases outside the table
      document.addEventListener('mouseup', documentMouseUpHandler, {once: true});
    },
    [parseLineNumberEvent, isPlusButtonEvent, setCellAtom, setDragStart, setDragCurrent, setSelectedLines, setHighlightDragStart, setHighlightDragCurrent, documentMouseUpHandler],
  );

  /** Mouse move: update the live drag highlight for either mode.
   *  Uses direct DOM class toggling for zero-overhead visual feedback.
   *  Also updates atoms for the line-change guard and final state. */
  const onMouseMoveLineNumber = useCallback(
    (event: React.MouseEvent<HTMLTableElement>) => {
      const table = dragTableRef.current ?? event.currentTarget;
      // Comment drag
      const commentDragStart = store.get(gitHubPullRequestDragStartAtom);
      if (commentDragStart != null) {
        const info = findLineFromRow(event, commentDragStart.side);
        if (info != null && info.path === commentDragStart.path) {
          const current = store.get(gitHubPullRequestDragCurrentAtom);
          if (current == null || current.lineNumber !== info.lineNumber) {
            setDragCurrent(info);
            applyDragHighlight(table, info.path, info.side, commentDragStart.lineNumber, info.lineNumber, 'comment');
          }
        }
        return;
      }
      // Highlight drag
      const highlightDragStart = store.get(gitHubPullRequestHighlightDragStartAtom);
      if (highlightDragStart != null) {
        const info = findLineFromRow(event, highlightDragStart.side);
        if (info != null && info.path === highlightDragStart.path) {
          const current = store.get(gitHubPullRequestHighlightDragCurrentAtom);
          if (current == null || current.lineNumber !== info.lineNumber) {
            setHighlightDragCurrent(info);
            applyDragHighlight(table, info.path, info.side, highlightDragStart.lineNumber, info.lineNumber, 'highlight');
          }
        }
        return;
      }
    },
    [store, findLineFromRow, setDragCurrent, setHighlightDragCurrent],
  );

  /** Mouse up: finish either comment or highlight selection.
   *  Uses findLineFromRow for robustness. */
  const onMouseUpLineNumber = useCallback(
    (event: React.MouseEvent<HTMLTableElement>) => {
      // Remove the document listener since the table handler fired first
      document.removeEventListener('mouseup', documentMouseUpHandler);

      // ---- Comment drag (from plus button) ----
      const commentDragStart = store.get(gitHubPullRequestDragStartAtom);
      if (commentDragStart != null) {
        const info = findLineFromRow(event, commentDragStart.side);
        completeCommentDrag(commentDragStart, info?.path === commentDragStart.path ? info : null);
        return;
      }

      // ---- Highlight drag (from line number) ----
      const highlightDragStart = store.get(gitHubPullRequestHighlightDragStartAtom);
      if (highlightDragStart != null) {
        const info = findLineFromRow(event, highlightDragStart.side);
        completeHighlightDrag(highlightDragStart, info?.path === highlightDragStart.path ? info : null);
        return;
      }
    },
    [store, findLineFromRow, documentMouseUpHandler, completeCommentDrag, completeHighlightDrag],
  );

  const onResetNewCommentInput = useCallback(() => {
    setCellAtom(null);
    setDragStart(null);
    setDragCurrent(null);
  }, [setCellAtom, setDragStart, setDragCurrent]);

  const newCommentInputCallbacks: NewCommentInputCallbacks | null = useMemo(() => {
    if (pullRequest != null) {
      return {onShowNewCommentInput, onMouseDownPlusButton, onMouseDownLineNumber, onMouseMoveLineNumber, onMouseUpLineNumber, onResetNewCommentInput};
    }
    return null;
  }, [pullRequest, onShowNewCommentInput, onMouseDownPlusButton, onMouseDownLineNumber, onMouseMoveLineNumber, onMouseUpLineNumber, onResetNewCommentInput]);

  // Read the hide-whitespace preference
  const ignoreWhitespace = useAtomValue(hideWhitespaceAtom);

  // Diff and tokenize atom
  const diffAndTokenizeParams = useMemo(
    () => ({path, before, after, scopeName, colorMode, ignoreWhitespace}),
    [path, before, after, scopeName, colorMode, ignoreWhitespace],
  );
  const diffAndTokenizeLoadableAtom = useMemo(
    () => loadable(diffAndTokenizeAtom(diffAndTokenizeParams)),
    [diffAndTokenizeParams],
  );
  const diffAndTokenizeLoadable = useAtomValue(diffAndTokenizeLoadableAtom);

  // Computed lineToPosition for this file path
  const computedLineToPositionLoadableAtom = useMemo(
    () => loadable(gitHubPullRequestComputedLineToPositionForFileAtom(path)),
    [path],
  );
  const lineToPositionLoadable = useAtomValue(computedLineToPositionLoadableAtom);

  // Sync the computed lineToPosition to the writable atom for other consumers
  useEffect(() => {
    if (isPullRequest && lineToPositionLoadable.state === 'hasData') {
      setJotaiLineToPosition(lineToPositionLoadable.data);
    }
  }, [isPullRequest, lineToPositionLoadable, setJotaiLineToPosition]);

  // Handle loading state from all sources
  // Also wait for versions to be loaded for PR case
  if (
    commitIDsLoadable.state === 'loading' ||
    diffAndTokenizeLoadable.state === 'loading' ||
    (isPullRequest && lineToPositionLoadable.state === 'loading')
  ) {
    return {state: 'loading'};
  }

  // PR case: versions haven't loaded yet, or commitIDs aren't yet available.
  // This handles the race condition where versions are still loading.
  if (
    isPullRequest &&
    (versionsLoadable.state !== 'hasData' ||
      versionsLoadable.data.length === 0 ||
      (commitIDsLoadable.state === 'hasData' && commitIDsLoadable.data == null))
  ) {
    return {state: 'loading'};
  }

  // Handle error state from all sources
  if (commitIDsLoadable.state === 'hasError') {
    return {state: 'hasError', error: commitIDsLoadable.error as Error};
  }
  if (diffAndTokenizeLoadable.state === 'hasError') {
    return {state: 'hasError', error: diffAndTokenizeLoadable.error as Error};
  }
  if (isPullRequest && lineToPositionLoadable.state === 'hasError') {
    return {state: 'hasError', error: lineToPositionLoadable.error as Error};
  }
  if (isPullRequest && versionsLoadable.state === 'hasError') {
    return {state: 'hasError', error: versionsLoadable.error as Error};
  }

  const diffAndTokenizeResult = diffAndTokenizeLoadable.data;
  const commitIDs = commitIDsLoadable.data;

  return {
    state: 'hasValue',
    data: {
      diffAndTokenize: diffAndTokenizeResult,
      // threads are now computed natively in Jotai
      threads,
      newCommentInputCallbacks,
      commitIDs,
    },
  };
}
